import { API_ORIGIN } from '../config';
// Authentication service for Festivals Malta portal
// Connects to existing PHP backend APIs

// Authentication service for Festivals Malta portal
// Connects to existing PHP backend APIs

const API_BASE_URL = `${API_ORIGIN}/api`;

class AuthService {

  async login(email, password) {
    try {
      const requestBody = { email, password };

      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include' // Re-enable for session cookies
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error(`Login failed: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(errorData.message || 'Login failed');
      }

      const userData = await response.json();
      
      // Store JWT token and user data
      if (userData.success) {
        // Store JWT token
        localStorage.setItem('authToken', userData.token);
        // Store user data (without sensitive info)
        localStorage.setItem('userSession', JSON.stringify(userData.user));
        return userData.user;
      } else {
        throw new Error(userData.message || 'Login failed');
      }
    } catch (error) {
      throw new Error(error.message || 'Login failed');
    }
  }

  async register(firstName, lastName, email, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          first_name: firstName, 
          last_name: lastName, 
          email, 
          password 
        }),
        credentials: 'include' // Re-enable for session cookies
      });

      const result = await response.json();

      // If the response has an error status or success: false, throw the error
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Registration failed');
      }

      return result;
    } catch (error) {
      // If it's a network error or JSON parsing error, handle it
      if (error.name === 'TypeError' || error.name === 'SyntaxError') {
        throw new Error('Network error or invalid response');
      }
      // Re-throw the error with the message from backend
      throw error;
    }
  }

  async logout() {
    try {
      const token = localStorage.getItem('authToken');
      
      // Call logout endpoint with JWT token if available
      if (token) {
        await fetch(`${API_BASE_URL}/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
      }
      
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('userSession');
    } catch (error) {
      // Clear local storage even if API call fails
      localStorage.removeItem('authToken');
      localStorage.removeItem('userSession');
    }
  }

  async checkSession() {
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        return null;
      }

      // Try to get user data from localStorage as fallback
      const storedUserData = localStorage.getItem('userSession');
      let fallbackUser = null;
      if (storedUserData) {
        try {
          fallbackUser = JSON.parse(storedUserData);
        } catch (e) {
          // ignore parse error
        }
      }

      const response = await fetch(`${API_BASE_URL}/verify-token`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        // Token is invalid, clear stored data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userSession');
        return null;
      }

      const data = await response.json();
      
      if (data.success) {
        // Get user roles from server - THIS IS THE SECURE PART
        const roleData = await this.getUserRoles();
        
        // Use data from verify-token endpoint, fallback to localStorage if needed
        const userData = data.user || fallbackUser;
        
        if (userData) {
          return {
            ...userData,
            isAdmin: roleData.isAdmin,
            isEmployeeAdmin: roleData.isEmployeeAdmin,
            isDirectOrderAdmin: roleData.isDirectOrderAdmin,
            isFinanceAdmin: roleData.isFinanceAdmin,
            isInventoryManager: roleData.isInventoryManager,
            isCfqAdmin: roleData.isCfqAdmin
          };
        }
      }
      
      return null;
    } catch (error) {
      // Session check failed, try fallback
      const storedUserData = localStorage.getItem('userSession');
      if (storedUserData) {
        try {
          const fallbackUser = JSON.parse(storedUserData);
          const roleData = await this.getUserRoles();
          return {
            ...fallbackUser,
            isAdmin: roleData.isAdmin,
            isEmployeeAdmin: roleData.isEmployeeAdmin,
            isDirectOrderAdmin: roleData.isDirectOrderAdmin,
            isFinanceAdmin: roleData.isFinanceAdmin,
            isInventoryManager: roleData.isInventoryManager,
            isCfqAdmin: roleData.isCfqAdmin
          };
        } catch (e) {
          // ignore
        }
      }
      return null;
    }
  }

  async getUserRoles() {
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        return { isAdmin: false, isEmployeeAdmin: false, isDirectOrderAdmin: false, isFinanceAdmin: false, isInventoryManager: false, isCfqAdmin: false };
      }

      const response = await fetch(`${API_BASE_URL}/user/roles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        // If token is invalid, clear stored data
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userSession');
        }
        return { isAdmin: false, isEmployeeAdmin: false, isDirectOrderAdmin: false, isFinanceAdmin: false, isInventoryManager: false, isCfqAdmin: false };
      }

      const data = await response.json();
      return {
        isAdmin: data.user?.isAdmin || false,
        isEmployeeAdmin: data.user?.isEmployeeAdmin || false,
        isDirectOrderAdmin: data.user?.isDirectOrderAdmin || false,
        isFinanceAdmin: data.user?.isFinanceAdmin || false,
        isInventoryManager: data.user?.isInventoryManager || false,
        isCfqAdmin: data.user?.isCfqAdmin || false
      };
    } catch (error) {
      return { isAdmin: false, isEmployeeAdmin: false, isDirectOrderAdmin: false, isFinanceAdmin: false, isInventoryManager: false, isCfqAdmin: false };
    }
  }

  async login(email, password) {
    try {
      const requestBody = { email, password };

      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error(`Login failed: ${response.status} ${response.statusText}`);
        }
        throw new Error(errorData.message || 'Login failed');
      }

      const userData = await response.json();
      
      if (userData.success) {
        // Store JWT token
        localStorage.setItem('authToken', userData.token);
        
        // Get roles immediately after login
        const roleData = await this.getUserRoles();
        
        const userWithRoles = {
          ...userData.user,
          isAdmin: roleData.isAdmin,
          isEmployeeAdmin: roleData.isEmployeeAdmin,
          isDirectOrderAdmin: roleData.isDirectOrderAdmin,
          isFinanceAdmin: roleData.isFinanceAdmin,
          isInventoryManager: roleData.isInventoryManager,
            isCfqAdmin: roleData.isCfqAdmin
        };
        
        // Store user data (without sensitive info)
        localStorage.setItem('userSession', JSON.stringify(userData.user));
        
        return userWithRoles;
      }
      
      throw new Error('Login failed');
    } catch (error) {
      throw error;
    }
  }

  // Background validation method
  validateSessionInBackground() {
    fetch(`${API_BASE_URL}/check-session`, {
      credentials: 'include'
    }).then(response => {
      if (!response.ok) {
        // Session is invalid, clear localStorage
        localStorage.removeItem('userSession');
      }
    }).catch(error => {
      // ignore background validation errors
    });
  }

  async resetPassword(email, currentPassword, newPassword, confirmPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          current_password: currentPassword, 
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Password reset failed');
      }

      return result;
    } catch (error) {
      if (error.name === 'TypeError' || error.name === 'SyntaxError') {
        throw new Error('Network error or invalid response');
      }
      throw error;
    }
  }

  async checkLineVotesAccess() {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/line-votes/access`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result.success === true;
    } catch (error) {
      return false;
    }
  }
}

export const authService = new AuthService();
